import { normalizeBannedPlayers } from '@parlay-picker/shared/analysisConfig';
import { AUTH_OPERATION_FALLBACKS } from '@parlay-picker/shared/authMessages';
import type { LeagueKey } from '@parlay-picker/shared/league';
import type { SportsbookId } from '@parlay-picker/shared/sportsbook';
import { type ApiErrorResponse, getUserFriendlyError, safeParseJson } from '@utils/api';
import { useCallback, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import { clearUserContext, setUserContext } from '../services/errorTracking';
import { fetchWithCredentials } from './authHttpUtils';
import type { AuthResponse, RegisterOptions, User, UserPreferences } from './authTypes';
import { normalizeUser } from './authTypes';
import { useToast } from './ToastContext';

const API_BASE = API_BASE_URL;

// =============================================================================
// Types
// =============================================================================

interface AuthOperationsDeps {
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  refetchUser: () => Promise<void>;
}

export interface AuthOperations {
  login: (email: string, password: string, rememberMe?: boolean) => Promise<User>;
  logout: () => Promise<void>;
  register: (
    email: string,
    password: string,
    options: RegisterOptions,
  ) => Promise<User | { type: 'verification_required'; email: string }>;
  resendVerificationPublic: (email: string) => Promise<AuthResponse>;
  requestPasswordReset: (email: string) => Promise<AuthResponse>;
  resetPassword: (token: string, newPassword: string) => Promise<AuthResponse>;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
  updateBannedPlayers: (bannedPlayers: string[], league: LeagueKey) => Promise<void>;
  updateProfile: (profile: {
    displayName?: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<AuthResponse>;
  verifyEmail: (token: string) => Promise<AuthResponse>;
  resendVerificationEmail: () => Promise<AuthResponse>;
  requestEmailChange: (newEmail: string, password: string) => Promise<AuthResponse>;
  cancelEmailChange: () => Promise<void>;
  acceptLegalDocuments: () => Promise<AuthResponse>;
  completeOnboarding: () => Promise<void>;
  deleteAccount: (password?: string) => Promise<void>;
  pauseAccount: (password?: string) => Promise<void>;
  oauthLogin: (
    provider: string,
    idToken: string,
    extra?: {
      displayName?: string;
      tosAccepted?: boolean;
      firstName?: string;
      lastName?: string;
      linkPassword?: string;
      inviteCode?: string;
    },
  ) => Promise<{
    type: string;
    user?: User;
    email?: string;
    provider?: string;
    firstName?: string;
    lastName?: string;
    deletedAt?: string;
    permanentDeletionDate?: string;
  }>;
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Provides all auth operation functions (login, register, update*, etc.)
 * as a hook so they can be extracted from AuthProvider while still having
 * access to shared state setters.
 */
export function useAuthOperations({
  authFetch,
  setUser,
  setError,
  refetchUser,
}: AuthOperationsDeps): AuthOperations {
  const { showToast, dismissAllToasts } = useToast();
  // Listen for chat-initiated sportsbook preference updates
  // biome-ignore lint/correctness/useExhaustiveDependencies: updatePreferences is stable within the provider
  useEffect(() => {
    const handler = (e: Event) => {
      const sportsbook = (e as CustomEvent<string>).detail;
      if (typeof sportsbook === 'string') {
        updatePreferences({ preferredSportsbook: sportsbook as SportsbookId });
      }
    };
    window.addEventListener('chat-update-sportsbook', handler);
    return () => window.removeEventListener('chat-update-sportsbook', handler);
  }, []);

  // Resend verification email (public, no auth required)
  const resendVerificationPublic = async (email: string): Promise<AuthResponse> => {
    try {
      const res = await fetchWithCredentials(`${API_BASE}/api/auth/resend-verification-public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      return await safeParseJson<AuthResponse>(res);
    } catch (err) {
      return { error: (err as Error).message };
    }
  };

  // Register
  const register = async (
    email: string,
    password: string,
    { firstName, lastName, displayName, tosAccepted, inviteCode }: RegisterOptions,
  ): Promise<User | { type: 'verification_required'; email: string }> => {
    setError(null);
    try {
      const res = await fetchWithCredentials(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName,
          displayName,
          tosAccepted,
          ...(inviteCode && { inviteCode }),
        }),
      });

      const data = await safeParseJson<AuthResponse & { type?: string; email?: string }>(res);

      if (!res.ok || (data as ApiErrorResponse).error) {
        throw new Error((data as ApiErrorResponse).error || AUTH_OPERATION_FALLBACKS.register);
      }

      // Email verification required — don't set user state
      if ('type' in data && data.type === 'verification_required') {
        return {
          type: 'verification_required',
          email: ('email' in data ? (data.email as string) : email) || email,
        };
      }

      const responseData = data as AuthResponse;
      if (responseData.user) {
        const normalizedUser = normalizeUser(responseData.user);
        setUser(normalizedUser);
        return normalizedUser;
      }
      throw new Error(AUTH_OPERATION_FALLBACKS.register);
    } catch (err) {
      const friendlyError = getUserFriendlyError(err as Error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  // Login
  const login = async (email: string, password: string, rememberMe = false): Promise<User> => {
    setError(null);
    try {
      const res = await fetchWithCredentials(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, rememberMe }),
      });

      const data = await safeParseJson<AuthResponse & { action?: string; email?: string }>(res);

      // Email not verified — throw structured error for AuthPage to handle
      if (res.status === 403 && 'action' in data && data.action === 'email_verification_required') {
        const verifyEmail = 'email' in data ? (data.email as string) : email;
        throw new Error(`email_verification_required:${verifyEmail || email}`);
      }

      // Account paused — throw for AuthPage to show resume modal
      if (res.status === 403 && 'action' in data && data.action === 'account_paused') {
        throw new Error('account_paused');
      }

      if (!res.ok || (data as ApiErrorResponse).error) {
        throw new Error((data as ApiErrorResponse).error || AUTH_OPERATION_FALLBACKS.login);
      }

      const responseData = data as AuthResponse;
      if (responseData.user) {
        const normalizedUser = normalizeUser(responseData.user);
        setUser(normalizedUser);

        // Set user context for error tracking
        setUserContext({
          id: normalizedUser.id,
          email: normalizedUser.email,
          role: normalizedUser.role,
          emailVerified: normalizedUser.emailVerified,
        });

        return normalizedUser;
      }
      throw new Error(AUTH_OPERATION_FALLBACKS.login);
    } catch (err) {
      // Re-throw structured auth errors unchanged so AuthPage can branch on
      // them (email verification, paused account). Otherwise the wrapper
      // below turns 'account_paused' into a human-readable string and the
      // resume modal never shows.
      if (err instanceof Error && err.message.startsWith('email_verification_required:')) {
        throw err;
      }
      if (err instanceof Error && err.message === 'account_paused') {
        throw err;
      }
      const friendlyError = getUserFriendlyError(err as Error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  // Logout
  const logout = async (): Promise<void> => {
    try {
      await fetchWithCredentials(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      // Logout API failure is non-critical — clear local state regardless
    }

    // Clear user context from error tracking
    clearUserContext();
    setUser(null);
    // Toasts from the signed-in session do not carry over to the landing page
    dismissAllToasts();
  };

  // Request password reset
  const requestPasswordReset = async (email: string): Promise<AuthResponse> => {
    setError(null);
    try {
      const res = await fetchWithCredentials(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await safeParseJson<AuthResponse>(res);

      if (!res.ok || (data as ApiErrorResponse).error) {
        throw new Error(
          (data as ApiErrorResponse).error || AUTH_OPERATION_FALLBACKS.forgotPassword,
        );
      }

      return data as AuthResponse;
    } catch (err) {
      const friendlyError = getUserFriendlyError(err as Error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  // Reset password with token
  const resetPassword = async (token: string, newPassword: string): Promise<AuthResponse> => {
    setError(null);
    try {
      const res = await fetchWithCredentials(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await safeParseJson<AuthResponse>(res);

      if (!res.ok || (data as ApiErrorResponse).error) {
        throw new Error((data as ApiErrorResponse).error || AUTH_OPERATION_FALLBACKS.resetPassword);
      }

      return data as AuthResponse;
    } catch (err) {
      const friendlyError = getUserFriendlyError(err as Error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  // Update preferences
  const updatePreferences = async (preferences: Partial<UserPreferences>): Promise<void> => {
    const res = await authFetch(`${API_BASE}/api/auth/preferences`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preferences }),
    }).catch(() => null);

    if (!res?.ok) {
      showToast("Couldn't save your settings. Try again.");
      return;
    }
    setUser((prev) =>
      prev
        ? {
            ...prev,
            preferences: { ...prev.preferences, ...preferences },
          }
        : null,
    );
  };

  // Update banned players
  const updateBannedPlayers = async (bannedPlayers: string[], league: LeagueKey): Promise<void> => {
    const res = await authFetch(`${API_BASE}/api/auth/banned-players`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bannedPlayers, league }),
    });

    // Thrown so the ban list can tell the user; the caller owns the message.
    if (!res.ok) throw new Error(`Ban list save failed (${res.status})`);
    setUser((prev) => {
      if (!prev) return null;
      const current = normalizeBannedPlayers(prev.bannedPlayers);
      current[league] = bannedPlayers;
      return { ...prev, bannedPlayers: current as unknown as string[] };
    });
  };

  // Update profile (display name, first name, last name)
  const updateProfile = async (profile: {
    displayName?: string;
    firstName?: string;
    lastName?: string;
  }): Promise<void> => {
    try {
      const res = await authFetch(`${API_BASE}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      });

      const data = await safeParseJson<{ user?: User }>(res);
      // Thrown so Settings shows why (a taken display name), not "Profile updated successfully".
      if (!res.ok) {
        throw new Error(('error' in data && data.error) || AUTH_OPERATION_FALLBACKS.updateProfile);
      }
      if (data && 'user' in data && data.user) {
        setUser(normalizeUser(data.user));
      }
    } catch (err) {
      throw new Error(getUserFriendlyError(err instanceof Error ? err : null));
    }
  };

  // Change password
  const changePassword = async (
    currentPassword: string,
    newPassword: string,
  ): Promise<AuthResponse> => {
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await safeParseJson<AuthResponse>(res);

      if (!res.ok || (data as ApiErrorResponse).error) {
        throw new Error(
          (data as ApiErrorResponse).error || AUTH_OPERATION_FALLBACKS.changePassword,
        );
      }

      return data as AuthResponse;
    } catch (err) {
      const friendlyError = getUserFriendlyError(err as Error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  // Verify email with token
  const verifyEmail = async (token: string): Promise<AuthResponse> => {
    setError(null);
    try {
      const res = await fetchWithCredentials(`${API_BASE}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const data = await safeParseJson<AuthResponse>(res);

      if (!res.ok || (data as ApiErrorResponse).error) {
        throw new Error((data as ApiErrorResponse).error || AUTH_OPERATION_FALLBACKS.verifyEmail);
      }

      const responseData = data as AuthResponse;

      // Auto-authenticate: server returns session cookies + user after verification
      if (responseData.user) {
        const normalizedUser = normalizeUser(responseData.user);
        setUser(normalizedUser);
        setUserContext({
          id: normalizedUser.id,
          email: normalizedUser.email,
          role: normalizedUser.role,
          emailVerified: normalizedUser.emailVerified,
        });
      } else {
        // Fallback: just update verified status on existing user
        setUser((prev) => (prev ? { ...prev, emailVerified: true } : null));
      }

      return responseData;
    } catch (err) {
      const friendlyError = getUserFriendlyError(err as Error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  // Resend verification email
  const resendVerificationEmail = async (): Promise<AuthResponse> => {
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await safeParseJson<AuthResponse>(res);

      if (!res.ok || (data as ApiErrorResponse).error) {
        throw new Error(
          (data as ApiErrorResponse).error || AUTH_OPERATION_FALLBACKS.resendVerification,
        );
      }

      return data as AuthResponse;
    } catch (err) {
      const friendlyError = getUserFriendlyError(err as Error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  // Request email change — sends verification to new address
  const requestEmailChange = async (newEmail: string, password: string): Promise<AuthResponse> => {
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/auth/request-email-change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newEmail, password }),
      });

      const data = await safeParseJson<AuthResponse>(res);

      if (!res.ok || (data as ApiErrorResponse).error) {
        throw new Error(
          (data as ApiErrorResponse).error || AUTH_OPERATION_FALLBACKS.requestEmailChange,
        );
      }

      // Refetch user to pick up pendingEmail
      await refetchUser();

      return data as AuthResponse;
    } catch (err) {
      const friendlyError = getUserFriendlyError(err as Error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  // Cancel a pending email change
  const cancelEmailChange = async (): Promise<void> => {
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/auth/cancel-email-change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        const data = await safeParseJson<ApiErrorResponse>(res);
        throw new Error(data.error || AUTH_OPERATION_FALLBACKS.cancelEmailChange);
      }

      // Refetch user to clear pendingEmail
      await refetchUser();
    } catch (err) {
      const friendlyError = getUserFriendlyError(err as Error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  // Accept updated legal documents (TOS/Privacy Policy)
  const acceptLegalDocuments = async (): Promise<AuthResponse> => {
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/auth/accept-legal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await safeParseJson<AuthResponse>(res);

      if (!res.ok || (data as ApiErrorResponse).error) {
        throw new Error((data as ApiErrorResponse).error || AUTH_OPERATION_FALLBACKS.acceptLegal);
      }

      // Update user state with new acceptance status
      const responseData = data as AuthResponse;
      if (responseData.user) {
        setUser(normalizeUser(responseData.user));
      }

      return data as AuthResponse;
    } catch (err) {
      const friendlyError = getUserFriendlyError(err as Error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  const completeOnboarding = async (): Promise<void> => {
    try {
      const res = await authFetch(`${API_BASE}/api/auth/onboarding/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await safeParseJson<AuthResponse>(res);

      if (!res.ok || (data as ApiErrorResponse).error) {
        throw new Error(
          (data as ApiErrorResponse).error || AUTH_OPERATION_FALLBACKS.completeOnboarding,
        );
      }

      // Update user state with new onboarding status
      const responseData = data as AuthResponse;
      if (responseData.user) {
        setUser(normalizeUser(responseData.user));
      }
    } catch {
      // Onboarding completion failure is non-critical
    }
  };

  // Delete account (soft-delete with 30-day recovery window)
  const deleteAccount = useCallback(
    async (password?: string): Promise<void> => {
      const res = await authFetch(`${API_BASE}/api/auth/account`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(password ? { password } : { verificationType: 'oauth' }),
      });
      if (!res.ok) {
        const data = await safeParseJson<ApiErrorResponse>(res);
        throw new Error(data.error || AUTH_OPERATION_FALLBACKS.deleteAccount);
      }
      // Clear auth state — user is now logged out
      clearUserContext();
      setUser(null);
    },
    [authFetch, setUser],
  );

  // Pause account (data preserved, subscription billing paused)
  const pauseAccount = useCallback(
    async (password?: string): Promise<void> => {
      const res = await authFetch(`${API_BASE}/api/auth/account/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(password ? { password } : { verificationType: 'oauth' }),
      });
      if (!res.ok) {
        const data = await safeParseJson<ApiErrorResponse>(res);
        throw new Error(data.error || AUTH_OPERATION_FALLBACKS.pauseAccount);
      }
      clearUserContext();
      setUser(null);
    },
    [authFetch, setUser],
  );

  // OAuth login/register
  const oauthLogin = async (
    provider: string,
    idToken: string,
    extra?: {
      displayName?: string;
      tosAccepted?: boolean;
      firstName?: string;
      lastName?: string;
      linkPassword?: string;
      inviteCode?: string;
    },
  ) => {
    setError(null);
    try {
      const res = await fetchWithCredentials(`${API_BASE}/api/auth/oauth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider, idToken, ...extra }),
      });

      const data = await res.json();

      if (data.type === 'success' && data.user) {
        const normalizedUser = normalizeUser(data.user);
        setUser(normalizedUser);
        setUserContext({
          id: normalizedUser.id,
          email: normalizedUser.email,
          role: normalizedUser.role,
          emailVerified: normalizedUser.emailVerified,
        });
        return { type: 'success', user: normalizedUser };
      }

      // Return non-success types for the caller to handle (linking, registration, deletion)
      return data;
    } catch (err) {
      const friendlyError = getUserFriendlyError(err as Error);
      setError(friendlyError);
      throw new Error(friendlyError);
    }
  };

  return {
    login,
    logout,
    register,
    resendVerificationPublic,
    requestPasswordReset,
    resetPassword,
    updatePreferences,
    updateBannedPlayers,
    updateProfile,
    changePassword,
    verifyEmail,
    resendVerificationEmail,
    requestEmailChange,
    cancelEmailChange,
    acceptLegalDocuments,
    completeOnboarding,
    deleteAccount,
    pauseAccount,
    oauthLogin,
  };
}
